import { useCallback, useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import { finishFlashcardSession, getNextFlashcard, saveFlashcardReview, suspendFlashcardInSession, completeStudyPlanTask } from "utils/supabase.js";
import { consumeStudyPlanTaskLink } from "utils/studyPlanLink";
import { useHistory, useParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import FlashcardsHeader from "components/Custom/FlashcardsHeader";
import FlashcardsBody from "components/Custom/FlashcardsBody";
import ProgresoFlashcards from "components/Custom/ProgresoFlashcards";
import AtajosFlashcards from "components/Custom/AtajosFlashcards";
import BackdropAlert from "components/Custom/BackdropAlert";

function isFlashcardSessionCompleted(data) {
    if (!data || typeof data !== "object") return false;

    const status = String(data?.status || data?.session_status || data?.session?.status || "").toLowerCase();
    const session = data?.session || {};

    return Boolean(
        data?.is_completed ||
        data?.completed_at ||
        data?.finished_at ||
        data?.ended_at ||
        session?.is_completed ||
        session?.completed_at ||
        session?.finished_at ||
        session?.ended_at ||
        status === "completed" ||
        status === "finished"
    );
}

export default function SesionFlashcards() {
    const { sessionId } = useParams();
    const history = useHistory();
    const [flashcardData, setFlashcardData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [backdropAlert, setBackdropAlert] = useState({
        open: false,
        severity: "info",
        title: "",
        message: "",
        description: "",
        closeOnBackdropClick: true,
        primaryAction: null,
        secondaryAction: null,
    });
    const bodyRef = useRef(null);
    const previousFlashcardIdRef = useRef(null);
    const finishedSessionRef = useRef(false);

    const closeBackdropAlert = useCallback(() => {
        setBackdropAlert((prev) => ({ ...prev, open: false }));
    }, []);

    const redirectToResults = useCallback(() => {
        history.push(`/admin/flashcards/${sessionId}/resultados`);
    }, [history, sessionId]);

    const showFinishedSessionAlert = useCallback(() => {
        finishedSessionRef.current = true;
        setBackdropAlert({
            open: true,
            severity: "info",
            title: "Sesión ya finalizada",
            message: "Esta sesión de flashcards ya fue finalizada.",
            description: "Pulsa ver resultados para revisar el resumen de la sesión.",
            closeOnBackdropClick: false,
            primaryAction: {
                label: "Ver resultados",
                onClick: () => {
                    closeBackdropAlert();
                    redirectToResults();
                },
            },
            secondaryAction: null,
        });
    }, [closeBackdropAlert, redirectToResults]);

    const showFetchNextErrorAlert = useCallback((error) => {
        const errorMessage = error?.message || error?.details || error?.hint || String(error || "");

        finishedSessionRef.current = true;
        setBackdropAlert({
            open: true,
            severity: "warning",
            title: "No se pudo cargar la tarjeta",
            message: errorMessage || "Hubo un problema al obtener la siguiente flashcard.",
            description: "Pulsa ver resultados para revisar el avance de la sesión.",
            closeOnBackdropClick: false,
            primaryAction: {
                label: "Ver resultados",
                onClick: () => {
                    closeBackdropAlert();
                    redirectToResults();
                },
            },
            secondaryAction: null,
        });
    }, [closeBackdropAlert, redirectToResults]);

    const fetchNextFlashcard = useCallback(async () => {
        try {
            const { data, error } = await getNextFlashcard(sessionId);
            if (error) {
                console.error("Error fetching next flashcard:", error);
                showFetchNextErrorAlert(error);
            } else {
                setFlashcardData(data);

                if (isFlashcardSessionCompleted(data)) {
                    showFinishedSessionAlert();
                    return;
                }

                if (!finishedSessionRef.current && (data?.has_next === false || !data?.flashcard)) {
                    finishedSessionRef.current = true;
                    const { error: finishError } = await finishFlashcardSession(sessionId);

                    if (finishError) {
                        console.error("Error finishing flashcard session:", finishError);
                    } else {
                        const linkedTaskId = consumeStudyPlanTaskLink("flashcards", sessionId);
                        if (linkedTaskId) {
                            const { error: completeError } = await completeStudyPlanTask(linkedTaskId, { flashcard_session_id: sessionId });
                            if (completeError) console.error("Error completing study plan task (flashcards):", completeError);
                        }

                        history.push(`/admin/flashcards/${sessionId}/resultados`);
                    }
                }
            }
        } catch (error) {
            console.error("Unexpected error:", error);
            showFetchNextErrorAlert(error);
        }
    }, [history, sessionId, showFetchNextErrorAlert, showFinishedSessionAlert]);

    useEffect(() => {
        fetchNextFlashcard();
    }, [fetchNextFlashcard]);

    useEffect(() => {
        const startedAt = Date.now();
        const intervalId = window.setInterval(() => {
            setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
        }, 1000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, []);

    useEffect(() => {
        const flashcardId = flashcardData?.flashcard?.id;

        if (!flashcardId) {
            return;
        }

        if (previousFlashcardIdRef.current && previousFlashcardIdRef.current !== flashcardId) {
            bodyRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }

        previousFlashcardIdRef.current = flashcardId;
    }, [flashcardData?.flashcard?.id]);

    async function handleReview(rating, responseTimeSeconds) {
        if (!flashcardData) {
            return;
        }

        setIsSaving(true);
        try {
            const { error } = await saveFlashcardReview(sessionId, flashcardData, rating, responseTimeSeconds);
            if (error) {
                console.error("Error saving flashcard review:", error);
                return;
            }

            await fetchNextFlashcard();
        } catch (error) {
            console.error("Unexpected error saving flashcard review:", error);
        } finally {
            setIsSaving(false);
        }
    }

    async function handleSuspend() {
        if (!flashcardData) {
            return;
        }

        setIsSaving(true);
        try {
            const { error } = await suspendFlashcardInSession(sessionId, flashcardData);
            if (error) {
                console.error("Error suspending flashcard:", error);
                return;
            }

            await fetchNextFlashcard();
        } catch (error) {
            console.error("Unexpected error suspending flashcard:", error);
        } finally {
            setIsSaving(false);
        }
    }

    const finishSession = useCallback(async () => {
        if (finishedSessionRef.current) {
            return;
        }

        setIsSaving(true);
        try {
            const { error } = await finishFlashcardSession(sessionId);
            if (error) {
                console.error("Error finishing flashcard session:", error);
                return;
            }

            const linkedTaskId = consumeStudyPlanTaskLink("flashcards", sessionId);
            if (linkedTaskId) {
                const { error: completeError } = await completeStudyPlanTask(linkedTaskId, { flashcard_session_id: sessionId });
                if (completeError) console.error("Error completing study plan task (flashcards):", completeError);
            }

            finishedSessionRef.current = true;
            setFlashcardData((prev) => ({
                ...(prev || {}),
                has_next: false,
                flashcard: null,
            }));
            history.push(`/admin/flashcards/${sessionId}/resultados`);
        } catch (error) {
            console.error("Unexpected error finishing flashcard session:", error);
        } finally {
            setIsSaving(false);
        }
    }, [history, sessionId]);

    const handleFinishSession = useCallback(() => {
        if (finishedSessionRef.current || isSaving) {
            return;
        }

        setBackdropAlert({
            open: true,
            severity: "warning",
            title: "Finalizar sesión",
            message: "¿Quieres finalizar esta sesión de flashcards?",
            description: "Después de finalizarla podrás revisar tus resultados.",
            closeOnBackdropClick: true,
            primaryAction: {
                label: "Finalizar sesión",
                color: "warning",
                onClick: () => {
                    closeBackdropAlert();
                    finishSession();
                },
            },
            secondaryAction: {
                label: "Cancelar",
                onClick: closeBackdropAlert,
            },
        });
    }, [closeBackdropAlert, finishSession, isSaving]);

    return (
        <>
        <BackdropAlert
            open={backdropAlert.open}
            onClose={closeBackdropAlert}
            severity={backdropAlert.severity}
            title={backdropAlert.title}
            message={backdropAlert.message}
            description={backdropAlert.description}
            closeOnBackdropClick={backdropAlert.closeOnBackdropClick}
            primaryAction={backdropAlert.primaryAction}
            secondaryAction={backdropAlert.secondaryAction}
        />
        <div className="flex flex-wrap">
            <div ref={bodyRef} className="w-full px-2">
                <FlashcardsHeader data={flashcardData} />
            </div>
            <div className="w-full px-2 mt-4">
                <div className="flex flex-wrap -mx-2">
                    <div className="w-full lg:w-8/12 px-2">
                        <FlashcardsBody
                            data={flashcardData}
                            loading={isSaving}
                            onReview={handleReview}
                            onSuspend={handleSuspend}
                            onFinish={handleFinishSession}
                        />
                        <div className="flex justify-center mt-4">
                            <Button
                                variant="outlined"
                                disabled={isSaving || finishedSessionRef.current}
                                onClick={handleFinishSession}
                                startIcon={<CheckCircle2 size={18} />}
                                sx={{
                                    borderColor: "#fdba74",
                                    borderRadius: "10px",
                                    color: "#f97316",
                                    fontWeight: 800,
                                    minWidth: { xs: "100%", sm: 240 },
                                    py: 1,
                                    textTransform: "none",
                                    "&:hover": {
                                        backgroundColor: "#fff7ed",
                                        borderColor: "#f97316",
                                        color: "#c2410c",
                                    },
                                    "&:focus": { outline: "none" },
                                    "&:focus-visible": { outline: "none" },
                                }}
                            >
                                Finalizar sesión
                            </Button>
                        </div>
                    </div>
                    <div className="hidden lg:block lg:w-4/12 px-2 lg:mt-0">
                        <ProgresoFlashcards data={flashcardData} elapsedSeconds={elapsedSeconds} />
                        <div className="mt-4">
                            <AtajosFlashcards />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
}